import { useState, useCallback, useMemo } from 'react'
import { Pencil, Save, X } from 'lucide-react'
import { formatCurrencyCompact } from '@/utils/format-currency'
import { useDealsStore } from '@/stores/useDealsStore'
import { isRocketInstance } from '@/config/instanceProfile'
import api from '@/services/api'
import { toast } from 'sonner'
import type { GoalTarget } from '@/types'

const AGRADA_CATEGORY_LABELS: Record<string, string> = {
  marketing: 'Marketing',
  support: 'Suporte',
  project: 'Projeto',
}

const ROCKET_CATEGORY_LABELS: Record<string, string> = {
  rocket: 'Rocket Search',
}

const CATEGORY_COLORS: Record<string, string> = {
  marketing: '#8b5cf6',
  support: '#10b981',
  project: '#f59e0b',
  rocket: '#d4145a',
}

interface GoalEditorProps {
  period: 'monthly' | 'quarterly' | 'annual'
  attainment: Record<string, number>
}

export function GoalEditor({ period, attainment }: GoalEditorProps) {
  const { goalTargets, setGoalTargets } = useDealsStore()
  const [editingCategory, setEditingCategory] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)

  const categoryLabels = useMemo(
    () => (isRocketInstance ? ROCKET_CATEGORY_LABELS : AGRADA_CATEGORY_LABELS),
    [],
  )

  const currentGoals = goalTargets[period] ?? {}

  const startEdit = useCallback((cat: string) => {
    setEditingCategory(cat)
    setEditValue(String(currentGoals[cat] ?? 0))
  }, [currentGoals])

  const cancelEdit = useCallback(() => {
    setEditingCategory(null)
    setEditValue('')
  }, [])

  const saveGoal = useCallback(async () => {
    if (!editingCategory) return
    const value = Number(editValue.replace(/[^\d.]/g, ''))
    if (Number.isNaN(value)) {
      toast.error('Valor inválido')
      return
    }
    setSaving(true)
    try {
      const newTargets: GoalTarget = {
        ...goalTargets,
        [period]: { ...currentGoals, [editingCategory]: value },
      }
      await api.post('/goals', {
        period,
        category: editingCategory,
        target: value,
      })
      setGoalTargets(newTargets)
      toast.success('Meta atualizada')
      setEditingCategory(null)
    } catch {
      toast.error('Erro ao salvar meta')
    } finally {
      setSaving(false)
    }
  }, [editingCategory, editValue, goalTargets, period, currentGoals, setGoalTargets])

  const gridClass = isRocketInstance ? 'grid grid-cols-1 gap-2' : 'grid grid-cols-3 gap-2'

  return (
    <div className={gridClass}>
      {Object.keys(categoryLabels).map((cat) => {
        const goal = currentGoals[cat] ?? 0
        const att = attainment[cat] ?? 0
        const pct = goal > 0 ? (att / goal) * 100 : 0
        const isEditing = editingCategory === cat

        return (
          <div
            key={cat}
            className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 space-y-2"
          >
            <div className="flex items-center gap-2">
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: CATEGORY_COLORS[cat] }}
              />
              <span className="text-text-primary text-xs font-medium">
                {categoryLabels[cat]}
              </span>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-text-muted text-xs">Meta</span>
                {isEditing ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void saveGoal()
                        if (e.key === 'Escape') cancelEdit()
                      }}
                      className="w-20 bg-bg-main border border-white/[0.1] rounded px-1.5 py-0.5 text-xs text-text-primary focus:outline-none focus:border-accent/40"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => void saveGoal()}
                      disabled={saving}
                      className="text-emerald-400 hover:text-emerald-300 transition-colors"
                    >
                      <Save size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="text-text-muted hover:text-red-400 transition-colors"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => startEdit(cat)}
                    className="flex items-center gap-1 text-text-primary text-xs font-semibold hover:text-accent transition-colors group"
                  >
                    {formatCurrencyCompact(goal)}
                    <Pencil size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-muted text-xs">Realizado</span>
                <span className="text-text-primary text-xs font-semibold">
                  {formatCurrencyCompact(att)}
                </span>
              </div>
              <div className="w-full h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(pct, 100)}%`,
                    backgroundColor: CATEGORY_COLORS[cat],
                  }}
                />
              </div>
              <p className="text-right text-xs font-medium" style={{ color: CATEGORY_COLORS[cat] }}>
                {`${pct.toFixed(1)}%`}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}
